// Page d'activation d'invitation
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, AlertCircle, CheckCircle2, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Activate() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invitations/check/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invitation invalide"));
  }, [token]);

  if (user) return <Navigate to="/app/dashboard" replace />;

  async function submit(e) {
    e.preventDefault();
    if (pw !== pw2) return setErr("Les mots de passe ne correspondent pas");
    if (pw.length < 8) return setErr("Mot de passe trop court");
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/invitations/activate", { token, password: pw });
      localStorage.setItem("rops_token", data.access_token);
      localStorage.setItem("rops_user", JSON.stringify(data.user));
      window.location.href = "/app/dashboard";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }

  if (!info && !err) {
    return <div className="min-h-screen grid place-items-center text-obsidian-400">Vérification…</div>;
  }

  if (err && !info) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-obsidian-950" data-testid="activate-page">
        <div className="card p-8 max-w-md text-center" data-testid="activate-error">
          <AlertCircle size={32} className="text-bad mx-auto mb-3" />
          <h2 className="font-heading text-xl font-bold mb-2">Invitation invalide</h2>
          <p className="text-sm text-obsidian-400">{err}</p>
          <a href="/login" className="btn-primary mt-6">Aller à la connexion</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-obsidian-950">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md" data-testid="activate-page">
        <div className="card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 grid place-items-center bg-citron rounded-sm">
              <Crown size={18} className="text-obsidian-950" />
            </div>
            <div>
              <div className="overline text-citron">Activation de compte</div>
              <h1 className="font-heading text-xl font-bold tracking-tight">Bienvenue {info.full_name}</h1>
            </div>
          </div>

          <div className="bg-obsidian-900 rounded-sm p-4 mb-5 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-obsidian-400">Email :</span><span className="font-mono">{info.email}</span></div>
            <div className="flex justify-between"><span className="text-obsidian-400">Rôle :</span><span className="text-citron font-semibold">{info.role}</span></div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label block">Choisissez votre mot de passe (min 8 caractères)</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-obsidian-500" />
                <input type="password" required minLength={8} className="input pl-10" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="activate-pw" />
              </div>
            </div>
            <div>
              <label className="label block">Confirmer</label>
              <input type="password" required className="input" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="activate-pw2" />
            </div>
            {err && <div className="text-bad text-xs p-2 bg-bad/10 border border-bad/30 rounded-sm">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="activate-submit">
              {loading ? "Activation…" : <>Activer mon compte <ArrowRight size={14} /></>}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
